/**
 * ProShip Direct — merchant API.
 *
 * Replaces the localStorage store for everything the backend now owns: the plan catalog, the
 * merchant's subscription, usage, offers and purchase. Backend: /api/direct/** (V10 schema),
 * gated per merchant on MerchantConfig.isProzoDirectEnabled — a merchant who is not switched on
 * gets a 400 saying so, which is the intended answer, not an error to hide.
 *
 * The merchant is taken from the JWT server-side; nothing here sends a merchant id.
 */
import { deleteGeneric, getGeneric, postGeneric, postMultipart, putGeneric } from '../../../common/constants';

const BASE = 'direct';

export const fetchCatalog      = () => getGeneric(`${BASE}/plans`);
export const fetchSubscription = () => getGeneric(`${BASE}/subscription`);
export const fetchUsage        = () => getGeneric(`${BASE}/usage`);
export const fetchLedger       = (page = 0, size = 50) => getGeneric(`${BASE}/ledger`, { page, size });
export const fetchCharges      = () => getGeneric(`${BASE}/charges`);

export const fetchOffers       = () => getGeneric(`${BASE}/offers`);
export const markOfferViewed   = (id) => postGeneric(`${BASE}/offers/${id}/viewed`, {});
export const respondToOffer    = (id, body) => postGeneric(`${BASE}/offers/${id}/respond`, body);

// ── courier setup (DIY) ─────────────────────────────────────────────────────
// Backed by proship_direct_courier_schema (V11) + PCIS. The credential FIELDS come from the
// server, never from a fixture: the key names must match what the PCIS adapter deserialises,
// and a wrong key produces an account that is created successfully and then fails at the first
// AWB. SYSTEM_DEFAULT fields (base URLs, protocol constants) are merged server-side and are
// deliberately absent from /schema — the merchant is never asked for them.

/** Couriers this merchant may bring a contract for: NATIVE in PCIS and with a credential schema. */
export const fetchCourierParents = () => getGeneric(`${BASE}/couriers/parents`);

/** cp_id candidates. Usually one; the merchant picks when several (DTDC). */
export const resolveCourierCp = (courierCode, direction, mode) =>
  getGeneric(`${BASE}/couriers/resolve`, { courierCode, direction, mode });

/**
 * The service-type catalogue — every active service_type_master row with its direction, mode,
 * weight band and category.
 *
 * Fetched whole (21 rows) rather than filtered per selection: the form narrows locally as the
 * merchant answers, and the four dropdowns are built from the data, so a code added later shows
 * up without a frontend change. `canInheritDefaults` says whether ProShip can lend this courier
 * and service its serviceability, zones and TAT.
 */
export const fetchServiceTypes = (courierCode) =>
  getGeneric(`${BASE}/couriers/service-types`, courierCode ? { courierCode } : {});

/** MERCHANT_INPUT fields only. */
export const fetchCourierSchema = (courierCode, direction) =>
  getGeneric(`${BASE}/couriers/schema`, { courierCode, direction });

/**
 * Tests credentials BEFORE they are saved — nothing is created in PCIS or ProShip either way.
 *
 * Takes the same body as registerCourier, deliberately: the server runs the identical credential
 * merge, so a pass here is a pass on exactly the payload Save will write. Returns
 * { success, message, testedAt }; a rejected credential is success:false with the courier's own
 * words, not a thrown error.
 */
export const testCourierCredentials = (body) => postGeneric(`${BASE}/couriers/test`, body);

/** Re-tests an already registered courier. Used from My Couriers, not from setup. */
export const testCourierAccount = (accountCode) =>
  postGeneric(`${BASE}/couriers/${encodeURIComponent(accountCode)}/test`, {});

/**
 * Whether an account code is still free, in ProShip and in PCIS. Always 200:
 * { accountCode, available, message } — message says why not. Save checks again regardless.
 */
export const checkCourierAccountCode = (accountCode) =>
  getGeneric(`${BASE}/couriers/account-code-check`, { accountCode });

/** Registers in PCIS and ProShip. Enforces the plan's courier limit. */
export const registerCourier = (body) => postGeneric(`${BASE}/couriers`, body);

export const fetchMyCouriers = () => getGeneric(`${BASE}/couriers`);

/**
 * Replaces the credentials on a registered courier — the way out of a FAILED account.
 *
 * Tested server-side before anything is written, so a rejected edit leaves a working account
 * working. Returns the same { success, message, testedAt } as the test endpoints: a carrier
 * rejection is success:false, not a thrown error.
 */
/**
 * One registered courier as the edit drawer opens it: limits as entered and credentials from
 * PCIS, every SECRET masked. Sending a mask back unchanged on save keeps the saved secret.
 */
export const fetchCourierAccount = (accountCode) =>
  getGeneric(`${BASE}/couriers/${encodeURIComponent(accountCode)}`);

export const updateCourierCredentials = (accountCode, body) =>
  putGeneric(`${BASE}/couriers/${encodeURIComponent(accountCode)}/credentials`, body);

/**
 * Pause or resume a courier. ProShip only — the PCIS account stays live deliberately, because
 * PCIS refuses every operation on an inactive account and shipments in flight still need tracking.
 */
export const setCourierActive = (accountCode, value) =>
  postGeneric(`${BASE}/couriers/${encodeURIComponent(accountCode)}/active?value=${value}`, {});

/** Retire for good. A tombstone, not a delete: dispatched shipments still have to resolve it. */
export const retireCourierApi = (accountCode) =>
  deleteGeneric(`${BASE}/couriers/${encodeURIComponent(accountCode)}`, {});

// ── L1 serviceability for a Direct courier ──────────────────────────────────

/** Current coverage, where it came from, and whether ProShip has any to lend. */
export const fetchCourierServiceability = (accountCode) =>
  getGeneric(`${BASE}/couriers/${encodeURIComponent(accountCode)}/serviceability`);

/**
 * Upload one, two or three single-column pincode files.
 *
 * Multipart, and parts are omitted rather than sent empty: an absent part leaves that list as it
 * was, so correcting the COD list is not a re-upload of all three.
 */
export const uploadCourierServiceability = (accountCode, { pickupPrepaid, dropPrepaid, dropCod }) => {
  const form = new FormData();
  if (pickupPrepaid) form.append('pickupPrepaid', pickupPrepaid);
  if (dropPrepaid) form.append('dropPrepaid', dropPrepaid);
  if (dropCod) form.append('dropCod', dropCod);
  return postMultipart(`${BASE}/couriers/${encodeURIComponent(accountCode)}/serviceability`, form);
};

/**
 * One coverage list as it stands — the merchant's own, or ProShip's if the courier is running on it.
 * `list` is pickupPrepaid | dropPrepaid | dropCod. Returns { list, count, pincodes }.
 */
export const fetchCourierServiceabilityList = (accountCode, list) =>
  getGeneric(`${BASE}/couriers/${encodeURIComponent(accountCode)}/serviceability/${list}`);

/** Copy ProShip's coverage for this carrier and service. A copy, so it is then theirs to edit. */
export const inheritCourierServiceability = (accountCode) =>
  postGeneric(`${BASE}/couriers/${encodeURIComponent(accountCode)}/serviceability/inherit`, {});

// ── zone mapping for a Direct courier ───────────────────────────────────────

/** Whose zones the courier runs on, whether ProShip's are reaching it, and any job in flight. */
export const fetchCourierZones = (accountCode) =>
  getGeneric(`${BASE}/couriers/${encodeURIComponent(accountCode)}/zones`);

/** Upload the matrix. Validated in full server-side before anything is written; the write is a job. */
export const uploadCourierZones = (accountCode, file) => {
  const form = new FormData();
  form.append('file', file);
  return postMultipart(`${BASE}/couriers/${encodeURIComponent(accountCode)}/zones`, form);
};

/** Use ProShip's zones — removes the merchant's own folder if they had one. */
export const resetCourierZonesToDefault = (accountCode) =>
  postGeneric(`${BASE}/couriers/${encodeURIComponent(accountCode)}/zones/use-default`, {});

/** Transit days per zone: the merchant's own, or ProShip's where they have none. */
export const fetchCourierTat = (accountCode) =>
  getGeneric(`${BASE}/couriers/${encodeURIComponent(accountCode)}/tat`);

/** Upload the merchant's TAT (zone, Min Tat days, Max Tat Days). Every problem comes back by line. */
export const uploadCourierTat = (accountCode, file) => {
  const form = new FormData();
  form.append('file', file);
  return postMultipart(`${BASE}/couriers/${encodeURIComponent(accountCode)}/tat`, form);
};

/** Use ProShip's TAT — removes the merchant's own, nothing is copied. */
export const resetCourierTatToDefault = (accountCode) =>
  postGeneric(`${BASE}/couriers/${encodeURIComponent(accountCode)}/tat/use-default`, {});

/** Buys. Debits the wallet (prepaid) or the credit line (postpaid). */
export const purchasePlan      = (body) => postGeneric(`${BASE}/purchase`, body);

/**
 * The payment code, on ProShip's OTP framework. Mails a code to the user's registered email and
 * answers { tokenId, sentTo }. The paying call carries { otpTokenId, otp } and the server checks
 * the code before it charges; there is no separate verify step.
 */
export const sendPaymentOtp = () => postGeneric(`${BASE}/otp`, {});
export const setAutoRenewApi   = (enabled) => postGeneric(`${BASE}/auto-renew?enabled=${enabled}`, {});

/** What moving up to this plan costs today and what it adds. Nothing is charged. */
export const fetchUpgradeQuote = (planId) => getGeneric(`${BASE}/upgrade/quote`, { planId });
/** Move up now: charges the pro-rata difference. Needs the OTP token. */
export const upgradePlanApi    = (planId, proof) => postGeneric(`${BASE}/upgrade`, { planId, ...proof });
/** The plan for the next period, the only way to a lower one. keepAccountCodes when it allows fewer couriers. */
export const setRenewalPlanApi = (planId, keepAccountCodes) =>
  postGeneric(`${BASE}/renewal-plan`, { planId, keepAccountCodes });
export const clearRenewalPlanApi = () => deleteGeneric(`${BASE}/renewal-plan`, {});
/** Pay a renewal that failed, now. Needs the OTP token. */
export const renewNowApi       = (proof) => postGeneric(`${BASE}/renew`, { ...proof });

/**
 * The backend writes merchant-readable sentences on 400 — "Insufficient wallet balance. This
 * costs 17700.00 and the balance is 4200.00 - short by 13500.00. Please recharge and try again."
 * The global handler prefixes "Input Data Error:"; strip it so the merchant sees the sentence.
 */
export const errText = (e) => {
  const raw = e?.response?.data?.message || e?.message || 'Something went wrong. Please try again.';
  return String(raw).replace(/^Input Data Error:\s*/i, '');
};

/**
 * Server PlanView -> the shape the existing screens already render.
 *
 * Deliberate mapping rather than a rename across nine files: `id` becomes the PLAN CODE, because
 * every consumer resolves a plan by the id held on the subscription and the subscription carries
 * planCode. The numeric row id is kept as `dbId` — that is what /purchase wants.
 *
 * NULL is a value, not missing data: shipments null = unlimited, periodDays null = no time bound.
 * Those stay null here so the UI can say the word rather than render 0.
 */
export const toPlanCard = (p) => ({
  dbId: p.id,
  id: p.planCode,
  name: p.name,
  price: p.planFee,
  gstAmount: p.gstAmount,
  totalPayable: p.totalPayable,
  gstRate: p.gstRate,
  shipments: p.shipmentsIncluded,
  overagePrice: p.overagePrice,
  courierIntegrations: p.courierIntegrationLimit,
  validityDays: p.periodDays,
  overdraftLimitUnits: p.overdraftLimitUnits,
  graceDays: p.graceDays,
  blurb: p.blurb,
  features: parseFeatures(p.featuresJson),
});

const parseFeatures = (json) => {
  if (!json) return [];
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v : [];
  } catch {
    // A malformed blob must not blank the pricing page — the bullets are decoration.
    return [];
  }
};
